// API Configuration
export const API_CONFIG = {
  BASE_URL: process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api',
  WEBSOCKET_URL: process.env.REACT_APP_WS_URL || 'ws://localhost:8000/ws',
  TIMEOUT: 30000, // 30 seconds
  RETRY_ATTEMPTS: 3,
  RETRY_DELAY: 1000, // 1 second
};

// API Endpoints
export const API_ENDPOINTS = {
  // Authentication
  LOGIN: '/auth/login',
  REGISTER: '/auth/register',
  LOGOUT: '/auth/logout',
  REFRESH_TOKEN: '/auth/refresh',
  PROFILE: '/auth/profile',
  
  // Market Data
  MARKET_DATA: '/market/ticker',
  HISTORICAL_DATA: '/market/history',
  MARKET_STATS: '/market/stats',
  ORDERBOOK: '/market/orderbook',
  TRADES: '/market/trades',
  
  // Trading
  PLACE_ORDER: '/trading/orders',
  CANCEL_ORDER: '/trading/orders',
  ORDERS: '/trading/orders',
  OPEN_ORDERS: '/trading/orders/open',
  TRADE_HISTORY: '/trading/trades',
  ORDER_ESTIMATE: '/trading/estimate',
  PORTFOLIO: '/trading/portfolio',
  BALANCE: '/trading/balance',
  POSITIONS: '/trading/positions',
  
  // User
  TRANSACTIONS: '/user/transactions',
  SETTINGS: '/user/settings',
  NOTIFICATIONS: '/user/notifications',
};

// Trading Constants - Export directly for easy importing
export const ORDER_TYPES = ['market', 'limit', 'stop', 'stop_limit'];
export const ORDER_SIDES = ['buy', 'sell'];

// Trading Pairs
export const TRADING_PAIRS = [
  { symbol: 'BTC/USDT', name: 'Bitcoin', baseAsset: 'BTC', quoteAsset: 'USDT' },
  { symbol: 'ETH/USDT', name: 'Ethereum', baseAsset: 'ETH', quoteAsset: 'USDT' },
  { symbol: 'BNB/USDT', name: 'Binance Coin', baseAsset: 'BNB', quoteAsset: 'USDT' },
  { symbol: 'ADA/USDT', name: 'Cardano', baseAsset: 'ADA', quoteAsset: 'USDT' },
  { symbol: 'SOL/USDT', name: 'Solana', baseAsset: 'SOL', quoteAsset: 'USDT' },
  { symbol: 'DOT/USDT', name: 'Polkadot', baseAsset: 'DOT', quoteAsset: 'USDT' },
  { symbol: 'MATIC/USDT', name: 'Polygon', baseAsset: 'MATIC', quoteAsset: 'USDT' },
  { symbol: 'AVAX/USDT', name: 'Avalanche', baseAsset: 'AVAX', quoteAsset: 'USDT' },
];

// Authentication Configuration
export const AUTH_CONFIG = {
  TOKEN_KEY: 'trading_app_token',
  REFRESH_TOKEN_KEY: 'trading_app_refresh_token',
  USER_KEY: 'trading_app_user',
  TOKEN_EXPIRY_BUFFER: 5 * 60 * 1000, // 5 minutes in milliseconds
};

// Error Messages
export const ERROR_MESSAGES = {
  NETWORK_ERROR: 'Network error. Please check your connection and try again.',
  SERVER_ERROR: 'Server error. Please try again later.',
  UNAUTHORIZED: 'Your session has expired. Please log in again.',
  FORBIDDEN: 'You do not have permission to perform this action.',
  NOT_FOUND: 'The requested resource was not found.',
  VALIDATION_ERROR: 'Please check your input and try again.',
  TIMEOUT_ERROR: 'Request timeout. Please try again.',
  UNKNOWN_ERROR: 'An unexpected error occurred. Please try again.',
};

// Trading Configuration
export const TRADING_CONFIG = {
  DEFAULT_SYMBOLS: ['BTC/USDT', 'ETH/USDT', 'BNB/USDT'],
  CHART_INTERVALS: ['1m', '5m', '15m', '1h', '4h', '1d', '1w'],
  ORDER_TYPES: ORDER_TYPES,
  ORDER_SIDES: ORDER_SIDES,
  MAX_PRECISION: 8,
};

// UI Configuration
export const UI_CONFIG = {
  THEME_KEY: 'trading_app_theme',
  LANGUAGE_KEY: 'trading_app_language',
  SIDEBAR_KEY: 'trading_app_sidebar_collapsed',
  DEFAULT_THEME: 'dark',
  DEFAULT_LANGUAGE: 'en',
  TOAST_DURATION: 5000,
  DEBOUNCE_DELAY: 300,
};

// WebSocket Events
export const WS_EVENTS = {
  CONNECT: 'connect',
  DISCONNECT: 'disconnect',
  AUTHENTICATE: 'authenticate',
  SUBSCRIBE: 'subscribe',
  UNSUBSCRIBE: 'unsubscribe',
  TICKER_UPDATE: 'ticker_update',
  ORDERBOOK_UPDATE: 'orderbook_update',
  TRADE_UPDATE: 'trade_update',
  PORTFOLIO_UPDATE: 'portfolio_update',
  ORDER_UPDATE: 'order_update',
  NOTIFICATION: 'notification',
};

// Local Storage Keys
export const STORAGE_KEYS = {
  ...AUTH_CONFIG,
  ...UI_CONFIG,
  TRADING_PREFERENCES: 'trading_app_preferences',
  WATCHLIST: 'trading_app_watchlist',
  CHART_SETTINGS: 'trading_app_chart_settings',
  DASHBOARD_LAYOUT: 'trading_app_dashboard_layout',
};

// Status Constants
export const STATUS = {
  IDLE: 'idle',
  LOADING: 'loading',
  SUCCESS: 'success',
  ERROR: 'error',
};

// Order Status
export const ORDER_STATUS = {
  PENDING: 'pending',
  OPEN: 'open',
  FILLED: 'filled',
  PARTIALLY_FILLED: 'partially_filled',
  CANCELLED: 'cancelled',
  REJECTED: 'rejected',
  EXPIRED: 'expired',
};

// Transaction Types
export const TRANSACTION_TYPES = {
  DEPOSIT: 'deposit',
  WITHDRAWAL: 'withdrawal',
  TRADE: 'trade',
  FEE: 'fee',
  TRANSFER: 'transfer',
  REWARD: 'reward',
  DIVIDEND: 'dividend',
};

// Notification Types
export const NOTIFICATION_TYPES = {
  INFO: 'info',
  SUCCESS: 'success',
  WARNING: 'warning',
  ERROR: 'error',
  TRADE: 'trade',
  DEPOSIT: 'deposit',
  WITHDRAWAL: 'withdrawal',
  SECURITY: 'security',
};

// Feature Flags
export const FEATURE_FLAGS = {
  ADVANCED_CHARTS: true,
  MARGIN_TRADING: false,
  FUTURES_TRADING: false,
  OPTIONS_TRADING: false,
  STAKING: false,
  LENDING: false,
  NFT_TRADING: false,
  COPY_TRADING: false,
  SOCIAL_FEATURES: false,
  MOBILE_APP: true,
  TWO_FACTOR_AUTH: true,
  EMAIL_NOTIFICATIONS: true,
  SMS_NOTIFICATIONS: false,
  PUSH_NOTIFICATIONS: true,
};

// Validation Rules
export const VALIDATION_RULES = {
  EMAIL: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  PASSWORD: {
    MIN_LENGTH: 8,
    REQUIRE_UPPERCASE: true,
    REQUIRE_LOWERCASE: true,
    REQUIRE_NUMBERS: true,
    REQUIRE_SYMBOLS: false,
  },
  PHONE: /^\+?[\d\s\-\(\)]+$/,
  USERNAME: {
    MIN_LENGTH: 3,
    MAX_LENGTH: 20,
    PATTERN: /^[a-zA-Z0-9_]+$/,
  },
};

// Default Configuration
export const DEFAULT_CONFIG = {
  API: API_CONFIG,
  AUTH: AUTH_CONFIG,
  TRADING: TRADING_CONFIG,
  UI: UI_CONFIG,
  STORAGE: STORAGE_KEYS,
};

// Cryptocurrency Configurations
export const CRYPTO_CURRENCIES = [
  { symbol: 'BTC', name: 'Bitcoin', decimals: 8 },
  { symbol: 'ETH', name: 'Ethereum', decimals: 8 },
  { symbol: 'BNB', name: 'Binance Coin', decimals: 8 },
  { symbol: 'ADA', name: 'Cardano', decimals: 6 },
  { symbol: 'SOL', name: 'Solana', decimals: 8 },
  { symbol: 'DOT', name: 'Polkadot', decimals: 8 },
  { symbol: 'MATIC', name: 'Polygon', decimals: 8 },
  { symbol: 'AVAX', name: 'Avalanche', decimals: 8 },
  { symbol: 'USDT', name: 'Tether', decimals: 6 },
  { symbol: 'USDC', name: 'USD Coin', decimals: 6 },
];

// Fiat Currency Configurations
export const FIAT_CURRENCIES = [
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
  { code: 'CNY', symbol: '¥', name: 'Chinese Yuan' },
  { code: 'KRW', symbol: '₩', name: 'South Korean Won' },
  { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
];